import { createAsyncThunk } from "@reduxjs/toolkit"

import {
  getAllBillingContactsForIssuer,
  getAllIssuers,
  getCurrentBillingContactForIssuer,
  getCurrentBillingContacts,
  getFutureBillingCotacts,
  getInstrumentsForListingContact,
  getIssuer,
  getIssuersOnSearch,
  getIssuersWithoutBillingContacts,
  getListingContactForIssuer,
  getPricingContactForIssuer,
  postOrgIssuerRelation
} from "./cruds"

const ACTION_TYPE_PARENT = "Issuers"

//* get current billing contact for the issuer
export const fetchCurrentBillingContact = createAsyncThunk(
  `${ACTION_TYPE_PARENT}/fetchCurrentBillingContact`,
  async (issuerId: number, { rejectWithValue }) => {
    try {
      const response = await getCurrentBillingContactForIssuer(issuerId)
      return response.data
    } catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err?.response?.data ?? "" })
    }
  }
)

//* get all listing contacts for the issuer
export const fetchListingContactForIssuer = createAsyncThunk(
  `${ACTION_TYPE_PARENT}/fetchListingContactForIssuer`,
  async ({ issuerId, requestHeaders }: any, { rejectWithValue }) => {
    try {
      const response = await getListingContactForIssuer(issuerId, requestHeaders)
      return response.data
    } catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

export const fetchInstrumentsForListingContact = createAsyncThunk(
  `${ACTION_TYPE_PARENT}/fetchInstrumentsForListingContact`,
  async ({ issuerId, contactId, requestHeaders }: any, { rejectWithValue }) => {
    try {
      const response = await getInstrumentsForListingContact(issuerId, contactId, requestHeaders)
      return response.data
    } catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err?.response?.data })
    }
  }
)

//* get all pricing contacts / relations for the issuer
export const fetchPricingContactsForIssuer = createAsyncThunk(
  `${ACTION_TYPE_PARENT}/fetchPricingContactsForIssuer`,
  async ({ issuerId, requestHeaders }: any, { rejectWithValue }) => {
    try {
      const response = await getPricingContactForIssuer(issuerId, requestHeaders)
      return response.data
    } catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

//* search issuers based on a search string
export const fetchIssuersOnSearch = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchIssuersOnSearch`, async (payload: any, { rejectWithValue }) => {
  try {
    const response = await getIssuersOnSearch(payload)
    return response.data
  } catch (err: any) {
    if (!err.response) {
      throw err
    }
    return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
  }
})

//* get issuer model based on issuer id
export const fetchIssuerForIssuerId = createAsyncThunk(
  `${ACTION_TYPE_PARENT}/fetchIssuerForIssuerId`,
  async (issuerId: number, { rejectWithValue }) => {
    try {
      const response = await getIssuer(issuerId)
      return response.data
    } catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

//* get future billing contacts for the issuer
export const fetchFutureBillingContactsByIssuerId = createAsyncThunk(
  `${ACTION_TYPE_PARENT}/fetchFutureBillingContactsByIssuerId`,
  async ({ issuerId, requestHeaders }: any, { rejectWithValue }) => {
    try {
      const response = await getFutureBillingCotacts(issuerId, requestHeaders)
      return response.data
    } catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

//* get all billing contacts for the issuer
export const fetchAllBillingContacts = createAsyncThunk(
  `${ACTION_TYPE_PARENT}/fetchAllBillingContacts`,
  async ({ issuerId, requestHeaders }: any, { rejectWithValue }) => {
    try {
      const response = await getAllBillingContactsForIssuer(issuerId, requestHeaders)
      return response.data
    } catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

export const fetchAllIssuers = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchAllIssuers`, async ({ requestHeaders }: any, { rejectWithValue }) => {
  try {
    const response = await getAllIssuers(requestHeaders)
    return { data: response.data, responseHeaders: response.headers }
  } catch (err: any) {
    if (!err.response) {
      throw err
    }
    return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
  }
})

// * returns current billing contacts for all issuers (Supervisor only)
export const fetchCurrentBillingContacts = createAsyncThunk(
  `${ACTION_TYPE_PARENT}/fetchCurrentBillingContacts`,
  async ({ requestHeaders = undefined }: any, { rejectWithValue }) => {
    try {
      const response = await getCurrentBillingContacts(requestHeaders)
      return response.data
    } catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

export const createOrgIssuerRelation = createAsyncThunk(
  `${ACTION_TYPE_PARENT}/createOrgIssuerRelation`,
  async ({ issuerId, payload }: any, { rejectWithValue }) => {
    try {
      const response = await postOrgIssuerRelation(issuerId, payload)
      return response.data
    } catch (err: any) {
      if (!err.response) {
        throw err
      }

      return rejectWithValue({ status: err.response.status, code: err.code, message: err?.response?.data })
    }
  }
)

//* returns a list of active issuers without billing contacts
export const fetchIssuersWithoutBillingContacts = createAsyncThunk(
  `${ACTION_TYPE_PARENT}/fetchIssuersWithoutBillingContacts`,
  async ({ requestHeaders = undefined }: any, { rejectWithValue }) => {
    try {
      const response = await getIssuersWithoutBillingContacts(requestHeaders)
      return { data: response.data, responseHeaders: response.headers }
    } catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err?.response?.data })
    }
  }
)